import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { NEW_MENU } from '../nav';
import { AlertsList, BellCount, useAlerts } from './Alerts';
import { ROLE_LABELS, initials, useLogout, useMe } from '../lib/session';
import { THEME_CHOICES, THEME_LABELS, useTheme } from '../lib/theme';
import { Icon } from './Icon';
import { SEARCH_EMPTY_HINT, SearchRows, useGlobalSearch } from './GlobalSearch';

type Open = 'novo' | 'alerts' | 'user' | null;

/** design/Topbar.dc.html: search, Novo, alerts bell, user menu. */
export function Topbar({ mini, onExpand }: { mini: boolean; onExpand: () => void }) {
  const [open, setOpen] = useState<Open>(null);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const search = useGlobalSearch(q);
  const clear = () => {
    setQ('');
    setActive(0);
  };
  const me = useMe();
  const alerts = useAlerts();
  const { choice, setChoice, dark } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const toggle = (which: Open) => setOpen((o) => (o === which ? null : which));
  const close = () => setOpen(null);

  return (
    <div style={{ position: 'relative', zIndex: 30 }}>
      {open || q ? (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => {
            close();
            clear();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 25,
            background: 'transparent',
            border: 0,
            cursor: 'default',
          }}
        />
      ) : null}
      <header className="topbar" style={{ zIndex: 26 }}>
        {mini ? (
          <button type="button" className="btn icon" aria-label="Expandir menu" onClick={onExpand}>
            <Icon name="menu" />
          </button>
        ) : null}
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 460 }}>
          <label className="search" style={{ maxWidth: 'none' }}>
            <Icon name="search" size={18} />
            <input
              aria-label="Buscar"
              placeholder="Buscar produto, SKU, cliente, pedido, carretel…"
              role="combobox"
              aria-expanded={!!q}
              aria-controls="search-results"
              aria-autocomplete="list"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                const n = search.results.length;
                if (e.key === 'Escape') clear();
                else if (e.key === 'ArrowDown' && n) {
                  e.preventDefault();
                  setActive((a) => (a + 1) % n);
                } else if (e.key === 'ArrowUp' && n) {
                  e.preventDefault();
                  setActive((a) => (a - 1 + n) % n);
                } else if (e.key === 'Enter' && search.results[active]) {
                  void navigate(search.results[active].href);
                  clear();
                }
              }}
            />
            {q ? (
              <button
                type="button"
                className="btn sm g"
                style={{ minHeight: 28, padding: '0 6px' }}
                onClick={clear}
                aria-label="Limpar busca"
              >
                ×
              </button>
            ) : null}
          </label>
          {q ? (
            <div
              id="search-results"
              className="menu"
              role="listbox"
              aria-label="Resultados"
              style={{
                position: 'absolute',
                top: 50,
                left: 0,
                right: 0,
                zIndex: 30,
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              {search.results.length ? (
                <SearchRows results={search.results} active={active} onPick={clear} />
              ) : (
                <div style={{ padding: '16px 12px' }} className="sm muted" role="status">
                  {search.loading
                    ? 'Buscando…'
                    : search.error
                      ? 'Não deu para buscar agora. Tente de novo.'
                      : `Nada encontrado para “${q.trim()}”. ${SEARCH_EMPTY_HINT}`}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className="grow" />
        <button
          type="button"
          className="btn p"
          aria-expanded={open === 'novo'}
          aria-haspopup="menu"
          onClick={() => toggle('novo')}
        >
          <Icon name="plus" size={18} strokeWidth={2.4} />
          Novo
        </button>
        <button
          type="button"
          className="btn icon"
          aria-label={alerts.unread ? `Alertas, ${alerts.unread} novos` : 'Alertas'}
          aria-expanded={open === 'alerts'}
          onClick={() => toggle('alerts')}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" />
          <BellCount unread={alerts.unread} />
        </button>
        <button
          type="button"
          className="btn icon"
          aria-label="Menu do usuário"
          aria-expanded={open === 'user'}
          onClick={() => toggle('user')}
          style={{ border: 0, background: 'transparent' }}
        >
          <span className={dark ? 'avatar co' : 'avatar'}>{initials(me.name)}</span>
        </button>

        {open === 'novo' ? (
          <div
            className="menu popover"
            role="menu"
            aria-label="Novo"
            style={{ right: 116, width: 290 }}
          >
            {NEW_MENU.map((item) => (
              <Link key={item.label} to={item.to} role="menuitem" onClick={close}>
                <Icon name={item.icon} />
                {item.label}
              </Link>
            ))}
          </div>
        ) : null}

        {open === 'alerts' ? (
          <section
            className="menu popover"
            aria-label="Alertas"
            style={{ right: 64, width: 390, padding: 8, gap: 2 }}
          >
            <AlertsList state={alerts} onPick={close} />
          </section>
        ) : null}

        {open === 'user' ? (
          <div
            className="menu popover"
            role="menu"
            aria-label="Usuário"
            style={{ right: 16, width: 280 }}
          >
            <div className="row" style={{ gap: 10, padding: '10px 12px 12px' }}>
              <span className={dark ? 'avatar co' : 'avatar'}>{initials(me.name)}</span>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontWeight: 600 }}>{me.name}</p>
                <p className="xs muted">{ROLE_LABELS[me.role]}</p>
              </div>
            </div>
            <div className="hr" />
            <div style={{ padding: '10px 12px' }} className="col">
              <span className="lbl" id="theme-label">
                Tema
              </span>
              <div className="seg" role="radiogroup" aria-labelledby="theme-label">
                {THEME_CHOICES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={choice === t}
                    className={choice === t ? 'on' : ''}
                    onClick={() => setChoice(t)}
                  >
                    {THEME_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
            <Link to="/configuracoes/usuarios" role="menuitem" onClick={close}>
              Usuários e sessões
            </Link>
            <Link to="/configuracoes" role="menuitem" onClick={close}>
              Configurações
            </Link>
            <div className="hr" />
            <button
              type="button"
              role="menuitem"
              style={{ color: 'var(--err)' }}
              onClick={() => {
                close();
                logout.mutate(undefined, { onSettled: () => void navigate('/login') });
              }}
            >
              Sair
            </button>
          </div>
        ) : null}
      </header>
    </div>
  );
}
